import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {DatePicker} from 'primeng/datepicker';
import {TableModule} from 'primeng/table';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {Fluid} from 'primeng/fluid';
import {IconComponent} from '../../components/app/layout/icon/icon.component';
import {ManufacturerService} from '../../manufacturer/manufacturer.service';
import {Observable} from 'rxjs';
import {ManufacturerCollection} from '../../manufacturer/manufacturer-collection';
import {AsyncPipe} from '@angular/common';
import {Select} from 'primeng/select';
import {SubsidiaryCollection} from '../../subsidiary/subsidiary-collection';
import {SubsidiaryService} from '../../subsidiary/subsidiary.service';
import {PlanningParameterService} from '../../planning-parameter/planning-parameter.service';
import {ForecastService} from '../../forecast/forecast.service';
import {PlanningParameter} from '../../planning-parameter/planning-parameter';
import {Manufacturer} from '../../manufacturer/manufacturer';
import {Forecast} from '../../forecast/forecast';
import {Tooltip} from 'primeng/tooltip';
import {Subsidiary} from '../../subsidiary/subsidiary';
import {ForecastDetail} from '../../forecast-detail/forecast-detail';
import {Badge} from 'primeng/badge';
import {ContextMenu} from 'primeng/contextmenu';
import {MenuItem} from 'primeng/api';
import {Router} from '@angular/router';
import {StorageService} from '../../services/app/storage.service';
import {AddForecastDetail} from '../../add-forecast-detail/add-forecast-detail';
import {ReviseForecastQuantity} from '../../revise-forecast-quantity/revise-forecast-quantity';
import {AllocateGoods} from '../../allocate-goods/allocate-goods';
import {ForecastDetailCollection} from '../../forecast-detail/forecast-detail-collection';
import {EnterNewForecast} from '../../enter-new-forecast/enter-new-forecast';

const FORECAST_PLANNING_LAST_FROM_DATE    = 'forecast_planning_last_from_date';
const FORECAST_PLANNING_LAST_TO_DATE      = 'forecast_planning_last_to_date';
const FORECAST_PLANNING_LAST_MANUFACTURER = 'forecast_planning_last_manufacturer';

@Component({
    selector: 'app-forecast-planning',
    imports: [
        DatePicker,
        TableModule,
        FormsModule,
        Button,
        Fluid,
        IconComponent,
        AsyncPipe,
        Select,
        Tooltip,
        Badge,
        ContextMenu,
        AddForecastDetail,
        ReviseForecastQuantity,
        AllocateGoods,
        EnterNewForecast
    ],
    templateUrl: './forecast-planning.html',
    styleUrl: './forecast-planning.scss',
    providers: []
})
export class ForecastPlanning implements OnInit {

    protected forecasts: Forecast[] = [];
    protected months: string[] = [];
    protected monthDates: Date[] = [];

    protected selectedDateRange: Date[] = [];
    protected selectedManufacturer: Manufacturer | null = null;
    protected selectedForecast!: Forecast;

    protected subsidiaries$!: Observable<SubsidiaryCollection>;
    protected manufacturers$!: Observable<ManufacturerCollection>;
    protected planningParameters!: PlanningParameter[];

    protected showEnterNewForecastModal  = false;
    protected showAddForecastDetailModal = false;
    protected showAllocateGoodsModal     = false;
    protected showReviseForecastModal    = false;

    protected expandedRows: Record<string, boolean> = {};
    protected forecastActions: MenuItem[] = [];

    // Cached to avoid recomputing on every change detection cycle
    protected uniquePlanningParameters: PlanningParameter[] = [];

    protected maxSelectableDate: Date | null = null;

    constructor(
        private subsidiaryService: SubsidiaryService,
        private manufacturerService: ManufacturerService,
        private planningParameterService: PlanningParameterService,
        private forecastService: ForecastService,
        private router: Router,
        private storage: StorageService,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.subsidiaries$   = this.subsidiaryService.getAll();
        this.manufacturers$  = this.manufacturerService.getAll();
        this.forecastActions = this.buildForecastActions();
        this.restoreLastSelection();
    }

    protected selectForecast(forecast: Forecast): void {
        this.selectedForecast = forecast;
    }

    protected dateRangeChanged(): void {
        if (this.selectedDateRange[0]) {
            const start = this.selectedDateRange[0];
            this.maxSelectableDate = new Date(start.getFullYear(), start.getMonth() + 12, 1);
        }

        if (!this.datesAreSet()) {
            return;
        }

        const [start, end] = this.selectedDateRange;
        const maxEnd = new Date(start.getFullYear(), start.getMonth() + 12, 1);

        if (end > maxEnd) {
            this.selectedDateRange = [start, maxEnd];
        }

        this.updateMonths();
        this.storage.set(FORECAST_PLANNING_LAST_FROM_DATE, this.selectedDateRange[0].toISOString());
        this.storage.set(FORECAST_PLANNING_LAST_TO_DATE, this.selectedDateRange[1].toISOString());

        if (this.selectedManufacturer instanceof Manufacturer) {
            this.loadForecasts(this.selectedManufacturer, this.selectedDateRange[0], this.selectedDateRange[1]);
        }
    }

    protected dateRangeCleared(): void {
        this.forecasts         = [];
        this.months            = [];
        this.selectedDateRange = [];
        this.storage.remove(FORECAST_PLANNING_LAST_FROM_DATE);
        this.storage.remove(FORECAST_PLANNING_LAST_TO_DATE);
    }

    protected manufacturerChanged(): void {
        const manufacturer = this.selectedManufacturer;
        this.storage.set(FORECAST_PLANNING_LAST_MANUFACTURER, manufacturer);

        if (!(manufacturer instanceof Manufacturer)) {
            this.forecasts          = [];
            this.planningParameters = [];
            return;
        }

        this.planningParameterService.getAll(manufacturer).subscribe(collection => {
            this.planningParameters = collection.getPlanningParameters();

            if (this.datesAreSet()) {
                this.loadForecasts(manufacturer, this.selectedDateRange[0], this.selectedDateRange[1]);
            }
        });
    }

    protected enterNewForecast(): void {
        this.showEnterNewForecastModal = true;
    }

    protected canCreateForecast(): boolean {
        return this.selectedManufacturer !== null && this.selectedManufacturer !== undefined;
    }

    protected addForecastDetail(forecast: Forecast): void {
        this.selectedForecast        = forecast;
        this.showAddForecastDetailModal = true;
    }

    protected onNewForecastEntered(newForecast: Forecast): void {
        this.forecasts.push(newForecast);
        this.updateUniquePlanningParameters();
    }

    protected onForecastDetailSaved(): void {
        if (this.datesAreSet() && this.selectedManufacturer) {
            this.loadForecasts(
                this.selectedManufacturer,
                this.selectedDateRange[0],
                this.selectedDateRange[1]
            );
        }
    }

    protected onGoodsAllocated(collection: ForecastDetailCollection): void {
        this.selectedForecast.forecastDetails = collection.getForecastDetails();
        this.cd.detectChanges();
    }

    protected findForecast(pp: PlanningParameter, monthDate: Date): Forecast | null {
        return this.forecasts.find(f =>
            f.planningParameter.id === pp.id &&
            f.month  === monthDate.getMonth() + 1 &&
            f.year   === monthDate.getFullYear()
        ) ?? null;
    }

    protected findForecastDetail(ppId: string, subsidiaryId: string, monthDate: Date): ForecastDetail | null {
        const forecast = this.forecasts.find(f =>
            f.planningParameter.id === ppId &&
            f.month  === monthDate.getMonth() + 1 &&
            f.year   === monthDate.getFullYear()
        );
        return forecast?.forecastDetails.find(d => d.subsidiary.id === subsidiaryId) ?? null;
    }

    protected getSubsidiariesForPP(pp: PlanningParameter): Subsidiary[] {
        const subsidiaryMap = new Map<string, Subsidiary>();
        for (const forecast of this.forecasts) {
            if (forecast.planningParameter.id === pp.id) {
                for (const detail of forecast.forecastDetails) {
                    subsidiaryMap.set(detail.subsidiary.id, detail.subsidiary);
                }
            }
        }
        return [...subsidiaryMap.values()].sort((a, b) => a.name.localeCompare(b.name));
    }

    protected getForecastStatusColor(forecast: Forecast): 'warn' | 'success' | 'danger' | 'info' | 'secondary' | 'contrast' {
        switch (forecast.status) {
            case 'confirmed': return 'success';
            case 'revised':   return 'warn';
            default:          return 'info';
        }
    }

    protected getForecastDisplayQuantity(forecast: Forecast): string {
        const lastRevision = forecast.forecastRevisions?.at(-1);
        return lastRevision
            ? `${forecast.requestedQuantity} (${lastRevision.quantity})`
            : forecast.requestedQuantity.toString();
    }

    protected getForecastTooltip(forecast: Forecast): string {
        const lastRevision = forecast.forecastRevisions?.at(-1);
        return lastRevision
            ? `Requested quantity : ${forecast.requestedQuantity}\nRevised quantity : ${lastRevision.quantity}\nStatus: ${forecast.status}`
            : forecast.requestedQuantity.toString();
    }

    protected onForecastRevised(): void {
        this.forecastService.getForecast(this.selectedForecast.id).subscribe(forecast => {
            const index = this.forecasts.findIndex(f => f.id === forecast.id);
            if (index !== -1) {
                this.forecasts[index] = forecast;
            }
        });
    }

    // -------------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------------

    private buildForecastActions(): MenuItem[] {
        return [
            {
                label: 'Allocate goods to legal entities',
                icon: 'pi pi-link',
                command: () => {
                    this.showAllocateGoodsModal = true;
                }
            },
            {
                label: 'Revise quantity',
                icon: 'pi pi-file-edit',
                command: () => {
                    this.showReviseForecastModal = true;
                }
            },
            {
                label: 'Confirm forecast',
                icon: 'pi pi-check-square'
            },
            {
                label: 'Edit forecast containers',
                icon: 'pi pi-pencil',
                command: () => this.router.navigate(['/forecasts', this.selectedForecast.id, 'containers'])
            }
        ];
    }

    private restoreLastSelection(): void {
        const dateFrom     = this.storage.get(FORECAST_PLANNING_LAST_FROM_DATE) as string;
        const dateTo       = this.storage.get(FORECAST_PLANNING_LAST_TO_DATE) as string;
        const manufacturer = this.storage.get(FORECAST_PLANNING_LAST_MANUFACTURER) as Manufacturer;

        if (dateFrom && dateTo) {
            this.selectedDateRange = [new Date(dateFrom), new Date(dateTo)];
        } else {
            const now = new Date();
            this.selectedDateRange = [
                new Date(now.getFullYear(), now.getMonth(), 1),
                new Date(now.getFullYear(), now.getMonth() + 6, 1),
            ];
        }

        this.updateMonths();

        if (manufacturer) {
            this.selectedManufacturer = manufacturer;
            this.planningParameterService.getAll(manufacturer).subscribe(collection => {
                this.planningParameters = collection.getPlanningParameters();
                this.loadForecasts(
                    manufacturer,
                    this.selectedDateRange[0],
                    this.selectedDateRange[1]
                );
            });
        }
    }

    private loadForecasts(
        manufacturer: Manufacturer,
        dateFrom: Date,
        dateTo: Date
    ): void {
        this.forecastService
            .getAll(manufacturer, dateFrom, dateTo)
            .subscribe(collection => {
                this.forecasts = collection.getForecasts();
                this.updateUniquePlanningParameters();
                this.cd.detectChanges();
            });
    }

    private updateMonths(): void {
        const result: { labels: string[]; dates: Date[] } = { labels: [], dates: [] };
        const cur = new Date(this.selectedDateRange[0].getFullYear(), this.selectedDateRange[0].getMonth(), 1);
        const end = new Date(this.selectedDateRange[1].getFullYear(), this.selectedDateRange[1].getMonth(), 1);

        while (cur <= end) {
            result.labels.push(cur.toLocaleString(undefined, { month: 'short', year: 'numeric' }));
            result.dates.push(new Date(cur));
            cur.setMonth(cur.getMonth() + 1);
        }

        this.months     = result.labels;
        this.monthDates = result.dates;
    }

    private updateUniquePlanningParameters(): void {
        const seen = new Set<string>();
        this.uniquePlanningParameters = this.forecasts
            .map(f => f.planningParameter)
            .filter(pp => {
                if (seen.has(pp.id)) return false;
                seen.add(pp.id);
                return true;
            });
    }

    private datesAreSet(): boolean {
        return this.selectedDateRange?.length === 2 &&
            this.selectedDateRange[0] instanceof Date &&
            this.selectedDateRange[1] instanceof Date;
    }
}
